import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Space, Spin, message } from "antd";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import type { MapMetaData } from "pages/mapViewManagement/mapSelector/types";
import formatUuidT from "../../../flatbuffers/display/uuid";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetBanksByOrganizationQuery } from "../../../redux/banks/bankAPI";
import {
  useLazyGetMapViewQuery,
  usePutMapViewMutation,
  usePostWaypointMutation,
  usePutWaypointMutation,
  useDeleteWaypointMutation,
  usePostGroupWaypointMutation,
  usePutGroupWaypointMutation,
  useDeleteGroupWaypointMutation,
  usePostBankWaypointMutation,
  usePutBankWaypointMutation,
  useDeleteBankWaypointMutation,
} from "../../../redux/mapView/mapViewAPI";
import validatePermission, { Permission } from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
import { RootState } from "../../../app/store";
import BankEditorDrawer from "./BankEditorDrawer";
import {
  BANK_LIFT_PIN_SPACING,
  DEFAULT_BANK_WAYPOINT_VIEW,
  DEFAULT_FONT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_GROUP_WAYPOINT_VIEW,
  DEFAULT_ICON_SIZE,
  DEFAULT_WAYPOINT_VIEW,
  getDefaultBankWidthForLiftCount,
  MIN_ICON_SIZE,
  MIN_SHAPE_SIZE,
  NO_MAP_FOUND_DESCRIPTION,
} from "./constants";
import GroupEditorDrawer from "./GroupEditorDrawer";
import LiftEditorDrawer from "./LiftEditorDrawer";
import SvgController from "./SvgController";
import type { Bank, BankWaypoint, GroupWaypoint, Waypoint } from "./types";

function resolvePublicAssetUrl(path: string): string {
  if (!path) {
    return path;
  }

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${normalizedPath}`;
}

function calculateGroupBounds(groupWaypoint: GroupWaypoint): GroupWaypoint {
  if (groupWaypoint.x === null || groupWaypoint.y === null) {
    return {
      ...groupWaypoint,
      minX: null,
      maxX: null,
      minY: null,
      maxY: null,
    };
  }

  const halfWidth = groupWaypoint.backgroundWidth / 2;
  const halfHeight = groupWaypoint.backgroundHeight / 2;

  return {
    ...groupWaypoint,
    minX: Math.round(groupWaypoint.x - halfWidth),
    maxX: Math.round(groupWaypoint.x + halfWidth),
    minY: Math.round(groupWaypoint.y - halfHeight),
    maxY: Math.round(groupWaypoint.y + halfHeight),
  };
}

function normalizeRotation(rotation: number): number {
  let next = rotation;
  while (next > 180) {
    next -= 360;
  }
  while (next < -180) {
    next += 360;
  }
  return Math.round(next);
}

function rotatePoint(x: number, y: number, centerX: number, centerY: number, deltaDeg: number) {
  const radians = (deltaDeg * Math.PI) / 180;
  const relX = x - centerX;
  const relY = y - centerY;

  return {
    x: centerX + relX * Math.cos(radians) - relY * Math.sin(radians),
    y: centerY + relX * Math.sin(radians) + relY * Math.cos(radians),
  };
}

function withBankBounds(bank: BankWaypoint): BankWaypoint {
  return calculateGroupBounds(bank) as BankWaypoint;
}

function toGroupManagedLiftWaypointId(
  groupId: string,
  bankName: string,
  liftName: string,
  liftIndex: number,
): string {
  const normalizedBank = bankName.trim().replace(/\s+/g, "_");
  const normalizedLift = liftName.trim().replace(/\s+/g, "_");
  return `group-lift-${groupId}-${normalizedBank}-${normalizedLift}-${liftIndex}`;
}

function toLiftOptionKey(bankName: string, liftName: string, liftIndex: number): string {
  return `${bankName}::${liftName}::${liftIndex}`;
}

function parseLiftOptionKey(
  key: string,
): { bankName: string; liftName: string; liftIndex: number } | null {
  const parts = key.split("::");
  if (parts.length !== 3) {
    return null;
  }

  const liftIndex = Number(parts[2]);
  if (Number.isNaN(liftIndex)) {
    return null;
  }

  return {
    bankName: parts[0],
    liftName: parts[1],
    liftIndex,
  };
}

function resolveLiftLinkId(bank: Bank | undefined, liftName: string, liftIndex: number): string {
  if (!bank) {
    return liftName;
  }

  return bank.liftIds?.[liftIndex] || liftName;
}

function toGroupAdditionalLiftWaypointId(
  groupId: string,
  bankName: string,
  liftName: string,
  liftIndex: number,
): string {
  const normalizedBank = bankName.trim().replace(/\s+/g, "_");
  const normalizedLift = liftName.trim().replace(/\s+/g, "_");
  return `group-extra-lift-${groupId}-${normalizedBank}-${normalizedLift}-${liftIndex}`;
}

function clampWaypointIntoGroupBounds(
  waypoint: Waypoint,
  groupWaypoint: GroupWaypoint | null | undefined,
): Waypoint {
  if (!groupWaypoint || waypoint.x === null || waypoint.y === null) {
    return waypoint;
  }

  const { minX, maxX, minY, maxY } = groupWaypoint;
  if (minX === null || maxX === null || minY === null || maxY === null) {
    return waypoint;
  }

  return {
    ...waypoint,
    x: Math.min(Math.max(waypoint.x, minX), maxX),
    y: Math.min(Math.max(waypoint.y, minY), maxY),
  };
}

function clampWaypointsIntoGroupBounds(
  waypoints: Waypoint[],
  groupWaypoint: GroupWaypoint | null | undefined,
): Waypoint[] {
  return waypoints.map((waypoint) => clampWaypointIntoGroupBounds(waypoint, groupWaypoint));
}

function resolveGroupSelectedBankNames(
  groupWaypoint: GroupWaypoint,
  bankWaypoints: BankWaypoint[],
): string[] {
  const namesFromBanks = (groupWaypoint.banks || [])
    .map(
      (selectedBank) =>
        bankWaypoints.find((bankWaypoint) => bankWaypoint.id === selectedBank.id)?.bankName ||
        selectedBank.bankName,
    )
    .filter((bankName): bankName is string => Boolean(bankName));

  if (namesFromBanks.length > 0) {
    return Array.from(new Set(namesFromBanks));
  }

  return [];
}

function parseManagedLiftIndexFromWaypointId(waypointId: string): number | null {
  const suffix = waypointId.split("-").pop();
  if (!suffix) {
    return null;
  }

  const parsed = Number(suffix);
  return Number.isNaN(parsed) ? null : parsed;
}

function inferGroupSelectionsFromManagedWaypoints(
  groupWaypoint: GroupWaypoint,
  allWaypoints: Waypoint[],
  bankWaypoints: BankWaypoint[],
  availableBanks: Bank[],
): Pick<GroupWaypoint, "banks" | "additionalLiftKeys"> {
  const managedPrefix = `group-lift-${groupWaypoint.id}-`;
  const additionalPrefix = `group-extra-lift-${groupWaypoint.id}-`;

  const managedWaypoints = allWaypoints.filter((waypoint) => waypoint.id.startsWith(managedPrefix));
  const additionalWaypoints = allWaypoints.filter((waypoint) =>
    waypoint.id.startsWith(additionalPrefix),
  );

  const inferredBankNames = Array.from(
    new Set(managedWaypoints.map((waypoint) => waypoint.linkedBank || "").filter(Boolean)),
  );

  const inferredAdditionalLiftKeys = Array.from(
    new Set(
      additionalWaypoints
        .map((waypoint) => {
          const bankName = waypoint.linkedBank || "";
          const linkedLiftId = waypoint.linkedLift || "";
          if (!bankName || !linkedLiftId) {
            return null;
          }

          const matchingBank = availableBanks.find((bank) => bank.name === bankName);
          if (!matchingBank) {
            return null;
          }

          const expectedIndex = parseManagedLiftIndexFromWaypointId(waypoint.id);
          const candidateIndexes =
            matchingBank.liftIds
              ?.map((candidateLiftId, index) => (candidateLiftId === linkedLiftId ? index : -1))
              .filter((index) => index >= 0) ?? [];
          const fallbackNameCandidateIndexes = matchingBank.lifts
            .map((candidateLiftName, index) => (candidateLiftName === linkedLiftId ? index : -1))
            .filter((index) => index >= 0);

          const resolvedCandidates =
            candidateIndexes.length > 0 ? candidateIndexes : fallbackNameCandidateIndexes;

          if (resolvedCandidates.length === 0) {
            return null;
          }

          const resolvedIndex =
            expectedIndex !== null && resolvedCandidates.includes(expectedIndex)
              ? expectedIndex
              : resolvedCandidates[0];

          const liftName = matchingBank.lifts[resolvedIndex];
          if (!liftName) {
            return null;
          }

          return toLiftOptionKey(bankName, liftName, resolvedIndex);
        })
        .filter((liftKey): liftKey is string => Boolean(liftKey)),
    ),
  );

  return {
    banks:
      Array.isArray(groupWaypoint.banks) && groupWaypoint.banks.length > 0
        ? groupWaypoint.banks
        : bankWaypoints.filter((bankWaypoint) =>
            inferredBankNames.includes(bankWaypoint.bankName ?? ""),
          ),
    additionalLiftKeys: inferredAdditionalLiftKeys,
  };
}

function buildGroupManagedWaypoints(
  groupWaypoint: GroupWaypoint,
  selectedBanks: Bank[],
  availableBanks: Bank[],
  additionalLiftKeys: string[],
  existingWaypoints: Waypoint[],
  makeNewWaypoint: () => Waypoint,
): Waypoint[] {
  const existingById = new Map(existingWaypoints.map((waypoint) => [waypoint.id, waypoint]));
  const additionalLiftKeySet = new Set(additionalLiftKeys);

  const bankManagedWaypoints = selectedBanks.flatMap((bank) =>
    bank.lifts
      .filter(
        (liftName, liftIndex) =>
          !additionalLiftKeySet.has(toLiftOptionKey(bank.name, liftName, liftIndex)),
      )
      .map((liftName, liftIndex) => {
        const waypointId = toGroupManagedLiftWaypointId(
          groupWaypoint.id,
          bank.name,
          liftName,
          liftIndex,
        );
        const existingWaypoint = existingById.get(waypointId);

        if (existingWaypoint) {
          return {
            ...existingWaypoint,
            mapId: existingWaypoint.mapId || groupWaypoint.mapId,
            nickname: `${liftName} (${bank.name})`,
            linkedBank: bank.name,
            linkedLift: resolveLiftLinkId(bank, liftName, liftIndex),
            liftGroup: groupWaypoint.nickname,
          };
        }

        return {
          ...makeNewWaypoint(),
          id: waypointId,
          mapId: groupWaypoint.mapId,
          nickname: `${liftName} (${bank.name})`,
          displayName: true,
          linkedBank: bank.name,
          linkedLift: resolveLiftLinkId(bank, liftName, liftIndex),
          liftGroup: groupWaypoint.nickname,
        };
      }),
  );

  const additionalManagedWaypoints = additionalLiftKeys.flatMap((liftKey) => {
    const parsedLift = parseLiftOptionKey(liftKey);
    if (!parsedLift) {
      return [];
    }

    const waypointId = toGroupAdditionalLiftWaypointId(
      groupWaypoint.id,
      parsedLift.bankName,
      parsedLift.liftName,
      parsedLift.liftIndex,
    );
    const existingWaypoint = existingById.get(waypointId);
    const matchingBank = availableBanks.find((bank) => bank.name === parsedLift.bankName);
    const linkedLiftId = resolveLiftLinkId(matchingBank, parsedLift.liftName, parsedLift.liftIndex);

    if (existingWaypoint) {
      return [
        {
          ...existingWaypoint,
          mapId: existingWaypoint.mapId || groupWaypoint.mapId,
          nickname: `${parsedLift.liftName} (${parsedLift.bankName})`,
          linkedBank: parsedLift.bankName,
          linkedLift: linkedLiftId,
          liftGroup: groupWaypoint.nickname,
        },
      ];
    }

    return [
      {
        ...makeNewWaypoint(),
        id: waypointId,
        mapId: groupWaypoint.mapId,
        nickname: `${parsedLift.liftName} (${parsedLift.bankName})`,
        displayName: true,
        linkedBank: parsedLift.bankName,
        linkedLift: linkedLiftId,
        liftGroup: groupWaypoint.nickname,
      },
    ];
  });

  return [...bankManagedWaypoints, ...additionalManagedWaypoints];
}

function normalizeWaypointFromMapData(waypoint: any): Waypoint {
  return {
    ...waypoint,
    id: formatUuidT(waypoint?.id),
    mapId: formatUuidT(waypoint?.mapId ?? waypoint?.map_id),
    position: typeof waypoint?.position === "number" ? waypoint.position : 0,
    linkedBank: waypoint?.linkedBank ?? waypoint?.linked_bank,
    linkedLift: waypoint?.linkedLift ?? waypoint?.linked_lift,
    liftGroup: waypoint?.liftGroup ?? waypoint?.lift_group,
  };
}

function normalizeGroupWaypointFromMapData(groupWaypoint: any): GroupWaypoint {
  const rawAdditionalLiftKeys =
    groupWaypoint?.additionalLiftKeys ?? groupWaypoint?.additional_lift_keys;
  const rawLifts = groupWaypoint?.lifts;
  const rawBanks = groupWaypoint?.banks;

  const normalizedLifts = Array.isArray(rawLifts)
    ? rawLifts.map((lift: any) => normalizeWaypointFromMapData(lift))
    : [];
  const normalizedBanks = Array.isArray(rawBanks)
    ? rawBanks.map((bank: any) => normalizeBankWaypointFromMapData(bank))
    : [];

  return {
    ...groupWaypoint,
    id: formatUuidT(groupWaypoint?.id),
    mapId: formatUuidT(groupWaypoint?.mapId ?? groupWaypoint?.map_id),
    position: typeof groupWaypoint?.position === "number" ? groupWaypoint.position : 0,
    lifts: normalizedLifts,
    banks: normalizedBanks,
    additionalLiftKeys: Array.isArray(rawAdditionalLiftKeys)
      ? rawAdditionalLiftKeys.map((liftKey: any) => String(liftKey))
      : [],
  };
}

function normalizeBankWaypointFromMapData(bankWaypoint: any): BankWaypoint {
  const normalizedLifts = Array.isArray(bankWaypoint?.lifts)
    ? bankWaypoint.lifts.map((lift: any) => normalizeWaypointFromMapData(lift))
    : [];

  return {
    ...bankWaypoint,
    id: formatUuidT(bankWaypoint?.id),
    mapId: formatUuidT(bankWaypoint?.mapId ?? bankWaypoint?.map_id),
    position: typeof bankWaypoint?.position === "number" ? bankWaypoint.position : 0,
    lifts: normalizedLifts,
  };
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(
      value,
      (_key, nestedValue) =>
        typeof nestedValue === "bigint" ? nestedValue.toString() : nestedValue,
      2,
    );
  } catch (error) {
    return `[unserializable payload: ${String(error)}]`;
  }
}

function MapEditorPage() {
  const location = useLocation();
  const { id } = useParams();
  const [triggerGetBanksByOrganization, { data: dataGetBanksByOrganization }] =
    useLazyGetBanksByOrganizationQuery();
  const [
    triggerGetMapView,
    { data: dataGetMapView, isLoading: isLoadingGetMapView, isFetching: isFetchingGetMapView },
  ] = useLazyGetMapViewQuery();
  const [triggerPutMapView, { isLoading: isLoadingPutMapView }] = usePutMapViewMutation();
  const [triggerPostWaypoint] = usePostWaypointMutation();
  const [triggerPutWaypoint] = usePutWaypointMutation();
  const [triggerDeleteWaypoint] = useDeleteWaypointMutation();
  const [triggerPostGroupWaypoint] = usePostGroupWaypointMutation();
  const [triggerPutGroupWaypoint] = usePutGroupWaypointMutation();
  const [triggerDeleteGroupWaypoint] = useDeleteGroupWaypointMutation();
  const [triggerPostBankWaypoint] = usePostBankWaypointMutation();
  const [triggerPutBankWaypoint] = usePutBankWaypointMutation();
  const [triggerDeleteBankWaypoint] = useDeleteBankWaypointMutation();
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const { permissions } = useSelector((state: RootState) => state.user);
  const resolvedOrganizationId =
    globalOrganizationId || userLogged.organizationId || userLogged.org?.id || "";

  const [openLiftEditorDrawer, setOpenLiftEditorDrawer] = useState<boolean>(false);
  const [openGroupEditorDrawer, setOpenGroupEditorDrawer] = useState<boolean>(false);
  const [openBankEditorDrawer, setOpenBankEditorDrawer] = useState<boolean>(false);
  const [isPinMode, setIsPinMode] = useState<boolean>(false);
  const [pinTarget, setPinTarget] = useState<"lift" | "group" | "bank" | null>(null);

  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [groupWaypoints, setGroupWaypoints] = useState<GroupWaypoint[]>([]);
  const [bankWaypoints, setBankWaypoints] = useState<BankWaypoint[]>([]);

  const [activeWaypoint, setActiveWaypoint] = useState<Waypoint | null>(null);
  const [activeGroupWaypoint, setActiveGroupWaypoint] = useState<GroupWaypoint | null>(null);
  const [activeGroupManagedLifts, setActiveGroupManagedLifts] = useState<Waypoint[]>([]);
  const [activeBankWaypoint, setActiveBankWaypoint] = useState<BankWaypoint | null>(null);
  const [activeBankLiftId, setActiveBankLiftId] = useState<string | null>(null);
  const [persistedBankWaypointKeys, setPersistedBankWaypointKeys] = useState<string[]>([]);
  const [editorViewportCenter, setEditorViewportCenter] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [isLiftSaveFlowLoading, setIsLiftSaveFlowLoading] = useState<boolean>(false);
  const [isGroupSaveFlowLoading, setIsGroupSaveFlowLoading] = useState<boolean>(false);
  const [isBankSaveFlowLoading, setIsBankSaveFlowLoading] = useState<boolean>(false);

  const isAnySaveFlowLoading =
    isLiftSaveFlowLoading || isGroupSaveFlowLoading || isBankSaveFlowLoading;
  let saveFlowLabel = "Saving waypoint and map...";
  if (isGroupSaveFlowLoading) {
    saveFlowLabel = "Saving group and map...";
  } else if (isBankSaveFlowLoading) {
    saveFlowLabel = "Saving bank and map...";
  }

  const [drawerMode, setDrawerMode] = useState<"create" | "edit">("create");
  const [groupDrawerMode, setGroupDrawerMode] = useState<"create" | "edit">("create");
  const [bankDrawerMode, setBankDrawerMode] = useState<"create" | "edit">("create");

  const makeNewWaypoint = useCallback(
    (): Waypoint => ({
      id: `waypoint-${Date.now()}`,
      mapId: id,
      position: 0,
      nickname: undefined,
      x: null,
      y: null,
      ...DEFAULT_WAYPOINT_VIEW,
      displayName: true,
      fontSize: DEFAULT_FONT_SIZE,
      fontColorHex: DEFAULT_FONT_COLOR,
    }),
    [id],
  );

  const makeNewGroupWaypoint = useCallback(
    (): GroupWaypoint => ({
      id: `group-waypoint-${Date.now()}`,
      mapId: id,
      position: 0,
      nickname: undefined,
      x: null,
      y: null,
      ...DEFAULT_GROUP_WAYPOINT_VIEW,
      displayName: true,
      fontSize: DEFAULT_FONT_SIZE,
      fontColorHex: DEFAULT_FONT_COLOR,
      minX: null,
      maxX: null,
      minY: null,
      maxY: null,
      lifts: [],
      banks: [],
      additionalLiftKeys: [],
    }),
    [id],
  );

  const makeNewBankWaypoint = useCallback(
    (): BankWaypoint => ({
      id: `bank-waypoint-${Date.now()}`,
      mapId: id,
      position: 0,
      nickname: undefined,
      bankName: undefined,
      x: null,
      y: null,
      ...DEFAULT_BANK_WAYPOINT_VIEW,
      displayName: true,
      fontSize: DEFAULT_FONT_SIZE,
      fontColorHex: DEFAULT_FONT_COLOR,
      minX: null,
      maxX: null,
      minY: null,
      maxY: null,
      lifts: [],
    }),
    [id],
  );

  const applyBankDeltaMove = (bank: BankWaypoint, deltaX: number, deltaY: number): BankWaypoint => {
    const nextLifts = bank.lifts.map((lift) => ({
      ...lift,
      x: lift.x === null ? null : lift.x + deltaX,
      y: lift.y === null ? null : lift.y + deltaY,
    }));

    return {
      ...bank,
      x: bank.x === null ? null : bank.x + deltaX,
      y: bank.y === null ? null : bank.y + deltaY,
      lifts: nextLifts,
    };
  };

  const applyBankResizeLinked = (
    previousBank: BankWaypoint,
    resizedBank: BankWaypoint,
  ): BankWaypoint => {
    if (
      !previousBank.linkLifts ||
      previousBank.x === null ||
      previousBank.y === null ||
      resizedBank.x === null ||
      resizedBank.y === null
    ) {
      return resizedBank;
    }

    const safeWidth = Math.max(previousBank.backgroundWidth, 1);
    const safeHeight = Math.max(previousBank.backgroundHeight, 1);
    const scaleX = resizedBank.backgroundWidth / safeWidth;
    const scaleY = resizedBank.backgroundHeight / safeHeight;

    const lifts = previousBank.lifts.map((lift) => {
      if (lift.x === null || lift.y === null) {
        return lift;
      }

      const relX = lift.x - previousBank.x!;
      const relY = lift.y - previousBank.y!;

      return {
        ...lift,
        x: Math.round(resizedBank.x! + relX * scaleX),
        y: Math.round(resizedBank.y! + relY * scaleY),
        backgroundWidth: Math.max(
          MIN_SHAPE_SIZE,
          Math.round(lift.backgroundWidth * Math.abs(scaleX)),
        ),
        backgroundHeight: Math.max(
          MIN_SHAPE_SIZE,
          Math.round(lift.backgroundHeight * Math.abs(scaleY)),
        ),
        iconWidth: Math.max(
          MIN_ICON_SIZE,
          Math.round((lift.iconWidth ?? DEFAULT_ICON_SIZE) * Math.abs(scaleX)),
        ),
        iconHeight: Math.max(
          MIN_ICON_SIZE,
          Math.round((lift.iconHeight ?? DEFAULT_ICON_SIZE) * Math.abs(scaleY)),
        ),
      };
    });

    return {
      ...resizedBank,
      lifts,
    };
  };

  const applyBankRotateLinked = (
    previousBank: BankWaypoint,
    rotatedBank: BankWaypoint,
  ): BankWaypoint => {
    if (!previousBank.linkLifts || previousBank.x === null || previousBank.y === null) {
      return rotatedBank;
    }

    const delta = normalizeRotation(
      (rotatedBank.backgroundRotationDeg ?? 0) - (previousBank.backgroundRotationDeg ?? 0),
    );

    const lifts = previousBank.lifts.map((lift) => {
      if (lift.x === null || lift.y === null) {
        return lift;
      }

      const nextPoint = rotatePoint(lift.x, lift.y, previousBank.x!, previousBank.y!, delta);

      return {
        ...lift,
        x: nextPoint.x,
        y: nextPoint.y,
        backgroundRotationDeg: normalizeRotation((lift.backgroundRotationDeg ?? 0) + delta),
        iconRotationDeg: normalizeRotation((lift.iconRotationDeg ?? 0) + delta),
        nameRotationDeg: normalizeRotation((lift.nameRotationDeg ?? 0) + delta),
      };
    });

    return {
      ...rotatedBank,
      lifts,
    };
  };

  const updateBankLift = (
    bank: BankWaypoint,
    liftId: string,
    updater: (lift: Waypoint) => Waypoint,
  ): BankWaypoint => ({
    ...bank,
    lifts: bank.lifts.map((lift) => (lift.id === liftId ? updater(lift) : lift)),
  });

  const findCommittedBankByLiftId = (liftId: string): BankWaypoint | undefined =>
    bankWaypoints.find((bankWaypoint) => bankWaypoint.lifts.some((lift) => lift.id === liftId));

  const placeBankLiftsAtPin = (
    bank: BankWaypoint,
    centerX: number,
    centerY: number,
  ): BankWaypoint => {
    const spacing = BANK_LIFT_PIN_SPACING;
    const startX = centerX - ((bank.lifts.length - 1) * spacing) / 2;

    const lifts = bank.lifts.map((lift, index) => ({
      ...lift,
      x: Math.round(startX + spacing * index),
      y: centerY,
    }));

    return {
      ...bank,
      x: centerX,
      y: centerY,
      lifts,
    };
  };

  const resolveInsertionCenter = (groupWaypoint: GroupWaypoint): { x: number; y: number } => {
    if (editorViewportCenter) {
      return editorViewportCenter;
    }

    if (groupWaypoint.x !== null && groupWaypoint.y !== null) {
      return {
        x: groupWaypoint.x,
        y: groupWaypoint.y,
      };
    }

    return { x: 0, y: 0 };
  };

  const centerIfUnpinned = (waypoint: Waypoint, center: { x: number; y: number }): Waypoint => {
    if (waypoint.x !== null && waypoint.y !== null) {
      return waypoint;
    }

    return {
      ...waypoint,
      x: center.x,
      y: center.y,
    };
  };

  const sanitizeBankWaypointName = (name: string) => name.trim().replace(/\s+/g, "_").toLowerCase();

  const createGroupAttachedBankWaypoint = (
    groupWaypoint: GroupWaypoint,
    bank: Bank,
    center: { x: number; y: number },
  ): BankWaypoint => {
    const bankWaypointId = `group-bank-${groupWaypoint.id}-${sanitizeBankWaypointName(bank.name)}`;
    const baseBankWaypoint: BankWaypoint = {
      ...makeNewBankWaypoint(),
      id: bankWaypointId,
      mapId: groupWaypoint.mapId,
      nickname: bank.name,
      bankName: bank.name,
      backgroundWidth: getDefaultBankWidthForLiftCount(bank.lifts.length),
      lifts: bank.lifts.map((liftName, liftIndex) => ({
        ...makeNewWaypoint(),
        id: `${bankWaypointId}-lift-${liftIndex}`,
        mapId: groupWaypoint.mapId,
        nickname: `${liftName} (${bank.name})`,
        linkedBank: bank.name,
        linkedLift: resolveLiftLinkId(bank, liftName, liftIndex),
        displayName: true,
      })),
    };

    return withBankBounds(placeBankLiftsAtPin(baseBankWaypoint, center.x, center.y));
  };

  const availableBanks: Bank[] = useMemo(() => {
    const banks: any[] = Array.isArray(dataGetBanksByOrganization?.data)
      ? dataGetBanksByOrganization.data
      : [];

    return banks
      .map((bank: any) => {
        const normalizedLifts = Array.isArray(bank?.lifts)
          ? (bank.lifts
              .map((lift: any, liftIndex: number) => {
                const liftName = String(lift?.name || lift?.nickname || lift?.label || "").trim();
                if (!liftName) {
                  return null;
                }

                const liftId =
                  formatUuidT(lift?.id ?? lift?.lift_id ?? lift?.liftId) ||
                  String(lift?.id || lift?.lift_id || lift?.liftId || `lift-${liftIndex}`).trim();

                return {
                  name: liftName,
                  id: liftId || liftName,
                };
              })
              .filter(Boolean) as Array<{ name: string; id: string }>)
          : [];

        return {
          name: String(bank?.name || ""),
          lifts: normalizedLifts.map((lift) => lift.name),
          liftIds: normalizedLifts.map((lift) => lift.id),
        };
      })
      .filter((bank: Bank) => bank.name.length > 0);
  }, [dataGetBanksByOrganization]);

  const displayedBankWaypoints = useMemo(() => {
    const boundedBanks = bankWaypoints.map((bankWaypoint) => withBankBounds(bankWaypoint));

    if (!openBankEditorDrawer || !activeBankWaypoint) {
      return boundedBanks;
    }

    const previewBank = withBankBounds(activeBankWaypoint);
    const existingIndex = boundedBanks.findIndex((item) => item.id === previewBank.id);

    if (existingIndex >= 0) {
      return boundedBanks.map((item) => (item.id === previewBank.id ? previewBank : item));
    }

    return [...boundedBanks, previewBank];
  }, [bankWaypoints, openBankEditorDrawer, activeBankWaypoint]);

  const linkedBankLockMap = useMemo(() => {
    const entries = displayedBankWaypoints
      .filter((bankWaypoint) => Boolean(bankWaypoint.bankName))
      .map((bankWaypoint) => [bankWaypoint.bankName as string, Boolean(bankWaypoint.linkLifts)]);

    return Object.fromEntries(entries);
  }, [displayedBankWaypoints]);

  const displayedWaypoints = useMemo(() => {
    let standaloneWaypoints = waypoints;

    if (openLiftEditorDrawer && activeWaypoint) {
      if (waypoints.some((item) => item.id === activeWaypoint.id)) {
        standaloneWaypoints = waypoints.map((item) =>
          item.id === activeWaypoint.id ? activeWaypoint : item,
        );
      } else {
        standaloneWaypoints = [...waypoints, activeWaypoint];
      }
    }

    const bankLiftWaypoints = displayedBankWaypoints.flatMap((bankWaypoint) => bankWaypoint.lifts);

    const baseWaypoints = [...standaloneWaypoints, ...bankLiftWaypoints];

    if (!openGroupEditorDrawer || !activeGroupWaypoint) {
      return baseWaypoints;
    }

    const managedPrefix = `group-lift-${activeGroupWaypoint.id}-`;
    const additionalPrefix = `group-extra-lift-${activeGroupWaypoint.id}-`;

    const nonManagedWaypoints = baseWaypoints.filter(
      (waypoint) =>
        !waypoint.id.startsWith(managedPrefix) && !waypoint.id.startsWith(additionalPrefix),
    );
    return [...nonManagedWaypoints, ...activeGroupManagedLifts];
  }, [
    openLiftEditorDrawer,
    activeWaypoint,
    waypoints,
    displayedBankWaypoints,
    openGroupEditorDrawer,
    activeGroupWaypoint,
    activeGroupManagedLifts,
  ]);

  const displayedGroupWaypoints = useMemo(() => {
    let standaloneGroups = groupWaypoints;

    if (openGroupEditorDrawer && activeGroupWaypoint) {
      if (groupWaypoints.some((item) => item.id === activeGroupWaypoint.id)) {
        standaloneGroups = groupWaypoints.map((item) =>
          item.id === activeGroupWaypoint.id ? calculateGroupBounds(activeGroupWaypoint) : item,
        );
      } else {
        standaloneGroups = [...groupWaypoints, calculateGroupBounds(activeGroupWaypoint)];
      }
    }

    return [...standaloneGroups, ...displayedBankWaypoints];
  }, [openGroupEditorDrawer, activeGroupWaypoint, groupWaypoints, displayedBankWaypoints]);

  useEffect(() => {
    const getInitialData = async () => {
      if (!resolvedOrganizationId) {
        return;
      }

      const token = await GETJwtToken();
      triggerGetMapView({
        token,
        body: {
          org_id: resolvedOrganizationId,
        },
      });

      triggerGetBanksByOrganization({
        token,
        page: 1,
        limit: 9999,
        searchAndFilterString: `&org_id=${resolvedOrganizationId}`,
      });
    };

    getInitialData();
  }, [resolvedOrganizationId, triggerGetBanksByOrganization, triggerGetMapView]);

  const mapRows: MapMetaData[] = useMemo(() => {
    const apiRows: any[] = Array.isArray(dataGetMapView?.data) ? dataGetMapView.data : [];

    return apiRows.map((item: any) => {
      let groupsSource: any[] = [];
      if (Array.isArray(item?.group_waypoints)) {
        groupsSource = item.group_waypoints;
      } else if (Array.isArray(item?.groupWaypoints)) {
        groupsSource = item.groupWaypoints;
      } else if (Array.isArray(item?.groups)) {
        groupsSource = item.groups;
      }

      let banksSource: any[] = [];
      if (Array.isArray(item?.bank_waypoints)) {
        banksSource = item.bank_waypoints;
      } else if (Array.isArray(item?.bankWaypoints)) {
        banksSource = item.bankWaypoints;
      } else if (Array.isArray(item?.banks)) {
        banksSource = item.banks;
      }

      return {
        id: formatUuidT(item?.id),
        mapName: item?.name || item?.mapName || "",
        company: item?.company || "",
        mapImgUrl: item?.map_img_url || item?.mapImgUrl || item?.offline_url || "",
        hasDisplayView: Boolean(item?.has_display_view ?? item?.hasDisplayView ?? false),
        isMain: Boolean(item?.is_main ?? item?.isMain ?? item?.main ?? false),
        main: Boolean(item?.main ?? item?.is_main ?? item?.isMain ?? false),
        waypoints: Array.isArray(item?.waypoints)
          ? item.waypoints.map(normalizeWaypointFromMapData)
          : [],
        groups: groupsSource.map(normalizeGroupWaypointFromMapData),
        banks: banksSource.map(normalizeBankWaypointFromMapData),
      } as MapMetaData;
    });
  }, [dataGetMapView]);

  useEffect(() => {
    if (!Array.isArray(dataGetMapView?.data)) {
      return;
    }

    // eslint-disable-next-line no-console
    console.info("[MapEditor] getMapView payload", safeJsonStringify(dataGetMapView.data));
  }, [dataGetMapView]);

  const selectedMap = useMemo(() => mapRows.find((item) => item.id === id), [id, mapRows]);

  useEffect(() => {
    if (!selectedMap) {
      return;
    }

    setWaypoints(
      (Array.isArray(selectedMap.waypoints) ? selectedMap.waypoints : []).map((waypoint) => ({
        ...waypoint,
        mapId: waypoint.mapId || selectedMap.id,
      })),
    );
    setGroupWaypoints(
      (Array.isArray(selectedMap.groups) ? selectedMap.groups : []).map((groupWaypoint) =>
        calculateGroupBounds({
          ...groupWaypoint,
          mapId: groupWaypoint.mapId || selectedMap.id,
          lifts: Array.isArray(groupWaypoint.lifts)
            ? groupWaypoint.lifts.map((lift) => ({
                ...lift,
                mapId: lift.mapId || selectedMap.id,
              }))
            : groupWaypoint.lifts,
          banks: Array.isArray(groupWaypoint.banks)
            ? groupWaypoint.banks.map((bankWaypoint) => ({
                ...bankWaypoint,
                mapId: bankWaypoint.mapId || selectedMap.id,
                lifts: Array.isArray(bankWaypoint.lifts)
                  ? bankWaypoint.lifts.map((lift) => ({
                      ...lift,
                      mapId: lift.mapId || selectedMap.id,
                    }))
                  : bankWaypoint.lifts,
              }))
            : groupWaypoint.banks,
        }),
      ),
    );
    setBankWaypoints(
      (Array.isArray(selectedMap.banks) ? selectedMap.banks : []).map((bankWaypoint) =>
        withBankBounds({
          ...bankWaypoint,
          mapId: bankWaypoint.mapId || selectedMap.id,
          lifts: Array.isArray(bankWaypoint.lifts)
            ? bankWaypoint.lifts.map((lift) => ({
                ...lift,
                mapId: lift.mapId || selectedMap.id,
              }))
            : bankWaypoint.lifts,
        }),
      ),
    );
    setPersistedBankWaypointKeys(
      (Array.isArray(selectedMap.banks) ? selectedMap.banks : []).map(
        (bankWaypoint) => bankWaypoint.id,
      ),
    );
  }, [selectedMap]);

  const organizationBankOptions = useMemo(
    () => availableBanks.map((bank) => ({ value: bank.name, label: bank.name })),
    [availableBanks],
  );

  const liftLinkOptions = useMemo(() => {
    const options = availableBanks.flatMap((bank) =>
      bank.lifts.map((liftName, liftIndex) => ({
        value: resolveLiftLinkId(bank, liftName, liftIndex),
        label: `${liftName} (${bank.name})`,
      })),
    );

    const dedupedByValue = new Map<string, { value: string; label: string }>();
    options.forEach((option) => {
      if (!option.value) {
        return;
      }
      if (!dedupedByValue.has(option.value)) {
        dedupedByValue.set(option.value, option);
      }
    });

    if (activeWaypoint?.linkedLift && !dedupedByValue.has(activeWaypoint.linkedLift)) {
      dedupedByValue.set(activeWaypoint.linkedLift, {
        value: activeWaypoint.linkedLift,
        label: activeWaypoint.linkedLift,
      });
    }

    return Array.from(dedupedByValue.values());
  }, [availableBanks, activeWaypoint?.linkedLift]);

  const allLiftOptionEntries = useMemo(
    () =>
      availableBanks.flatMap((bank) =>
        bank.lifts.map((liftName, liftIndex) => ({
          key: toLiftOptionKey(bank.name, liftName, liftIndex),
          bankName: bank.name,
          liftName,
          liftIndex,
          label: `${bank.name} - ${liftName}`,
        })),
      ),
    [availableBanks],
  );

  const selectedGroupBankLiftKeySet = useMemo(() => {
    if (!activeGroupWaypoint) {
      return new Set<string>();
    }

    const selectedBankNames = resolveGroupSelectedBankNames(activeGroupWaypoint, bankWaypoints);
    const selectedBanks = availableBanks.filter((bank) => selectedBankNames.includes(bank.name));
    const selectedAdditional = new Set(activeGroupWaypoint.additionalLiftKeys || []);

    return new Set(
      selectedBanks.flatMap((bank) =>
        bank.lifts
          .map((liftName, liftIndex) => toLiftOptionKey(bank.name, liftName, liftIndex))
          .filter((liftKey) => !selectedAdditional.has(liftKey)),
      ),
    );
  }, [activeGroupWaypoint, availableBanks, bankWaypoints]);

  const additionalLiftOptions = useMemo(
    () =>
      allLiftOptionEntries
        .filter((entry) => !selectedGroupBankLiftKeySet.has(entry.key))
        .map((entry) => ({ value: entry.key, label: entry.label })),
    [allLiftOptionEntries, selectedGroupBankLiftKeySet],
  );

  useEffect(() => {
    if (!openGroupEditorDrawer || !activeGroupWaypoint) {
      setActiveGroupManagedLifts((previous) => (previous.length === 0 ? previous : []));
      return;
    }

    const selectedBankNames = resolveGroupSelectedBankNames(activeGroupWaypoint, bankWaypoints);
    const selectedBanks = availableBanks.filter((bank) => selectedBankNames.includes(bank.name));
    const existingManagedPool = [...waypoints, ...(activeGroupWaypoint.lifts || [])];
    const previewManagedWaypoints = clampWaypointsIntoGroupBounds(
      buildGroupManagedWaypoints(
        activeGroupWaypoint,
        selectedBanks,
        availableBanks,
        activeGroupWaypoint.additionalLiftKeys || [],
        existingManagedPool,
        makeNewWaypoint,
      ),
      activeGroupWaypoint,
    );

    setActiveGroupManagedLifts((previous) => {
      const previousById = new Map(previous.map((waypoint) => [waypoint.id, waypoint]));
      return previewManagedWaypoints.map((waypoint) => {
        const existingWaypoint = previousById.get(waypoint.id);
        return existingWaypoint
          ? clampWaypointIntoGroupBounds(existingWaypoint, activeGroupWaypoint)
          : waypoint;
      });
    });
  }, [
    openGroupEditorDrawer,
    activeGroupWaypoint,
    availableBanks,
    bankWaypoints,
    waypoints,
    makeNewWaypoint,
  ]);

  const requireSelectedMapId = (): string => {
    if (!selectedMap?.id) {
      throw new Error("Missing map id for waypoint save");
    }
    return selectedMap.id;
  };

  const resolvePersistedEntityId = (response: unknown): string => {
    if (typeof response === "string") {
      return response;
    }

    if (!response || typeof response !== "object") {
      return "";
    }

    const responseRecord = response as Record<string, unknown>;
    const candidateKeys = [
      "id",
      "waypoint_id",
      "waypointId",
      "group_waypoint_id",
      "groupWaypointId",
      "bank_waypoint_id",
      "bankWaypointId",
    ];

    const matchedKey = candidateKeys.find((key) => {
      const value = responseRecord[key];
      const formatted = formatUuidT(value as Parameters<typeof formatUuidT>[0]);
      if (formatted) {
        return true;
      }

      if (typeof value === "string" && value) {
        return true;
      }

      return false;
    });

    if (!matchedKey) {
      return "";
    }

    const matchedValue = responseRecord[matchedKey];
    const formatted = formatUuidT(matchedValue as Parameters<typeof formatUuidT>[0]);
    if (formatted) {
      return formatted;
    }

    if (typeof matchedValue === "string") {
      return matchedValue;
    }

    return "";
  };

  const withPersistedId = <T extends { id: string }>(entity: T, response: unknown): T => {
    const persistedId = resolvePersistedEntityId(response);
    if (!persistedId || persistedId === entity.id) {
      return entity;
    }

    return {
      ...entity,
      id: persistedId,
    };
  };

  const buildWaypointMutationBody = (waypoint: Waypoint) => ({
    id: waypoint.id,
    nickname: waypoint.nickname ?? "",
    x: waypoint.x ?? 0,
    y: waypoint.y ?? 0,
    background_color_hex: waypoint.backgroundColorHex,
    background_opacity: waypoint.backgroundOpacity,
    background_width: waypoint.backgroundWidth,
    background_height: waypoint.backgroundHeight,
    display_name: waypoint.displayName,
    font_size: waypoint.fontSize,
    font_color_hex: waypoint.fontColorHex,
    background_rotation_deg: waypoint.backgroundRotationDeg ?? 0,
    icon_rotation_deg: waypoint.iconRotationDeg ?? 0,
    name_rotation_deg: waypoint.nameRotationDeg ?? 0,
    sync_rotations: waypoint.syncRotations ?? true,
    icon_width: waypoint.iconWidth ?? 0,
    icon_height: waypoint.iconHeight ?? 0,
    maintain_icon_aspect_ratio: waypoint.maintainIconAspectRatio ?? true,
    linked_lift: waypoint.linkedLift ?? "",
  });

  const buildGroupWaypointMutationBody = (groupWaypoint: GroupWaypoint) => ({
    id: groupWaypoint.id,
    nickname: groupWaypoint.nickname ?? "",
    x: groupWaypoint.x ?? 0,
    y: groupWaypoint.y ?? 0,
    background_color_hex: groupWaypoint.backgroundColorHex,
    background_opacity: groupWaypoint.backgroundOpacity,
    background_width: groupWaypoint.backgroundWidth,
    background_height: groupWaypoint.backgroundHeight,
    display_name: groupWaypoint.displayName,
    font_size: groupWaypoint.fontSize,
    font_color_hex: groupWaypoint.fontColorHex,
    background_rotation_deg: groupWaypoint.backgroundRotationDeg ?? 0,
    name_rotation_deg: groupWaypoint.nameRotationDeg ?? 0,
    sync_rotations: groupWaypoint.syncRotations ?? true,
    min_x: groupWaypoint.minX ?? 0,
    max_x: groupWaypoint.maxX ?? 0,
    min_y: groupWaypoint.minY ?? 0,
    max_y: groupWaypoint.maxY ?? 0,
    lifts: Array.isArray(groupWaypoint.lifts)
      ? groupWaypoint.lifts.map(buildWaypointMutationBody)
      : [],
    banks: Array.isArray(groupWaypoint.banks)
      ? groupWaypoint.banks.map(buildBankWaypointMutationBody)
      : [],
  });

  const buildBankWaypointMutationBody = (bankWaypoint: BankWaypoint) => ({
    id: bankWaypoint.id,
    nickname: bankWaypoint.nickname ?? "",
    bank_name: bankWaypoint.bankName ?? "",
    x: bankWaypoint.x ?? 0,
    y: bankWaypoint.y ?? 0,
    background_color_hex: bankWaypoint.backgroundColorHex,
    background_opacity: bankWaypoint.backgroundOpacity,
    background_width: bankWaypoint.backgroundWidth,
    background_height: bankWaypoint.backgroundHeight,
    display_name: bankWaypoint.displayName,
    font_size: bankWaypoint.fontSize,
    font_color_hex: bankWaypoint.fontColorHex,
    background_rotation_deg: bankWaypoint.backgroundRotationDeg ?? 0,
    name_rotation_deg: bankWaypoint.nameRotationDeg ?? 0,
    sync_rotations: bankWaypoint.syncRotations ?? true,
    min_x: bankWaypoint.minX ?? 0,
    max_x: bankWaypoint.maxX ?? 0,
    min_y: bankWaypoint.minY ?? 0,
    max_y: bankWaypoint.maxY ?? 0,
    link_lifts: bankWaypoint.linkLifts,
    lifts: Array.isArray(bankWaypoint.lifts)
      ? bankWaypoint.lifts.map(buildWaypointMutationBody)
      : [],
  });

  const persistWaypoint = async (
    waypoint: Waypoint,
    mode: "create" | "edit",
  ): Promise<Waypoint> => {
    const token = await GETJwtToken();
    const body = buildWaypointMutationBody(waypoint);
    const mapId = waypoint.mapId || requireSelectedMapId();

    if (mode === "create") {
      const response = await triggerPostWaypoint({ token, map_id: mapId, body }).unwrap();
      return withPersistedId(waypoint, response);
    }

    await triggerPutWaypoint({ token, id: waypoint.id, map_id: mapId, body }).unwrap();
    return waypoint;
  };

  const removeWaypoint = async (waypointId: string) => {
    const token = await GETJwtToken();
    await triggerDeleteWaypoint({ token, id: waypointId }).unwrap();
  };

  const persistGroupWaypoint = async (
    groupWaypoint: GroupWaypoint,
    mode: "create" | "edit",
  ): Promise<GroupWaypoint> => {
    const token = await GETJwtToken();
    const body = buildGroupWaypointMutationBody(groupWaypoint);
    const mapId = groupWaypoint.mapId || requireSelectedMapId();

    if (mode === "create") {
      const response = await triggerPostGroupWaypoint({ token, map_id: mapId, body }).unwrap();
      return withPersistedId(groupWaypoint, response);
    }

    await triggerPutGroupWaypoint({ token, id: groupWaypoint.id, map_id: mapId, body }).unwrap();
    return groupWaypoint;
  };

  const removeGroupWaypoint = async (groupWaypointId: string) => {
    const token = await GETJwtToken();
    await triggerDeleteGroupWaypoint({ token, id: groupWaypointId }).unwrap();
  };

  const persistBankWaypoint = async (
    bankWaypoint: BankWaypoint,
    mode: "create" | "edit",
  ): Promise<BankWaypoint> => {
    const token = await GETJwtToken();
    const body = buildBankWaypointMutationBody(bankWaypoint);
    const mapId = bankWaypoint.mapId || requireSelectedMapId();

    if (mode === "create") {
      const response = await triggerPostBankWaypoint({ token, map_id: mapId, body }).unwrap();
      return withPersistedId(bankWaypoint, response);
    }

    await triggerPutBankWaypoint({ token, id: bankWaypoint.id, map_id: mapId, body }).unwrap();
    return bankWaypoint;
  };

  const removeBankWaypoint = async (bankWaypointId: string) => {
    const token = await GETJwtToken();
    await triggerDeleteBankWaypoint({ token, id: bankWaypointId }).unwrap();
  };

  const upsertWaypointById = (items: Waypoint[], nextWaypoint: Waypoint): Waypoint[] => {
    const existingIndex = items.findIndex((item) => item.id === nextWaypoint.id);
    if (existingIndex >= 0) {
      return items.map((item) => (item.id === nextWaypoint.id ? nextWaypoint : item));
    }
    return [...items, nextWaypoint];
  };

  const upsertGroupWaypointById = (
    items: GroupWaypoint[],
    nextGroupWaypoint: GroupWaypoint,
  ): GroupWaypoint[] => {
    const existingIndex = items.findIndex((item) => item.id === nextGroupWaypoint.id);
    if (existingIndex >= 0) {
      return items.map((item) => (item.id === nextGroupWaypoint.id ? nextGroupWaypoint : item));
    }
    return [...items, nextGroupWaypoint];
  };

  const upsertBankWaypointById = (
    items: BankWaypoint[],
    nextBankWaypoint: BankWaypoint,
  ): BankWaypoint[] => {
    const existingIndex = items.findIndex((item) => item.id === nextBankWaypoint.id);
    if (existingIndex >= 0) {
      return items.map((item) => (item.id === nextBankWaypoint.id ? nextBankWaypoint : item));
    }
    return [...items, nextBankWaypoint];
  };

  const persistMapSnapshot = async (
    nextWaypoints: Waypoint[],
    nextGroupWaypoints: GroupWaypoint[],
    nextBankWaypoints: BankWaypoint[],
  ) => {
    if (!selectedMap || !resolvedOrganizationId) {
      throw new Error("Missing map context for save");
    }

    const token = await GETJwtToken();
    await triggerPutMapView({
      token,
      id: selectedMap.id,
      body: {
        id: selectedMap.id,
        name: selectedMap.mapName,
        org_id: resolvedOrganizationId,
        map_img_url: selectedMap.mapImgUrl || "",
        has_display_view: Boolean(selectedMap.hasDisplayView),
        is_main: Boolean(selectedMap.isMain ?? selectedMap.main ?? false),
        offline_url: "",
        waypoints: nextWaypoints,
        group_waypoints: nextGroupWaypoints,
        bank_waypoints: nextBankWaypoints,
      },
    }).unwrap();
  };

  const saveMapChanges = async () => {
    if (!selectedMap || !resolvedOrganizationId) {
      return;
    }

    try {
      await persistMapSnapshot(waypoints, groupWaypoints, bankWaypoints);

      message.success("Map changes saved.");
    } catch {
      message.error("Unable to save map changes.");
    }
  };

  const applyGroupBankSelection = (groupWaypoint: GroupWaypoint, selectedBankNames: string[]) => {
    const center = resolveInsertionCenter(groupWaypoint);
    const bankNames = Array.from(
      new Set(selectedBankNames.map((bankName) => String(bankName).trim()).filter(Boolean)),
    );
    const inDrawerOnlyBanks = (groupWaypoint.banks || []).filter(
      (drawerBank) => !bankWaypoints.some((committedBank) => committedBank.id === drawerBank.id),
    );
    const knownBanks = [...bankWaypoints, ...inDrawerOnlyBanks];

    const missingBankWaypoints = bankNames
      .filter((bankName) => !knownBanks.some((bankWaypoint) => bankWaypoint.bankName === bankName))
      .map((bankName) => {
        const bank = availableBanks.find((candidateBank) => candidateBank.name === bankName);
        if (!bank) {
          return null;
        }

        return createGroupAttachedBankWaypoint(groupWaypoint, bank, center);
      })
      .filter((bankWaypoint): bankWaypoint is BankWaypoint => Boolean(bankWaypoint));

    if (missingBankWaypoints.length > 0) {
      setBankWaypoints((previous) => {
        const byId = new Map(previous.map((bankWaypoint) => [bankWaypoint.id, bankWaypoint]));
        missingBankWaypoints.forEach((bankWaypoint) => {
          byId.set(bankWaypoint.id, bankWaypoint);
        });
        return Array.from(byId.values());
      });
    }

    const selectedAttachedBankWaypoints = [...knownBanks, ...missingBankWaypoints].filter(
      (bankWaypoint) => bankNames.includes(bankWaypoint.bankName ?? ""),
    );
    const selectedBanks = availableBanks.filter((bank) => bankNames.includes(bank.name));
    const bankLiftKeys = new Set(
      selectedBanks.flatMap((bank) =>
        bank.lifts.map((liftName, liftIndex) => toLiftOptionKey(bank.name, liftName, liftIndex)),
      ),
    );
    const nextAdditionalLiftKeys = (groupWaypoint.additionalLiftKeys || []).filter(
      (liftKey) => !bankLiftKeys.has(liftKey),
    );
    const managedWaypoints = buildGroupManagedWaypoints(
      groupWaypoint,
      selectedBanks,
      availableBanks,
      nextAdditionalLiftKeys,
      [...waypoints, ...(groupWaypoint.lifts || [])],
      makeNewWaypoint,
    ).map((waypoint) => centerIfUnpinned(waypoint, center));

    const nextGroupWaypoint = calculateGroupBounds({
      ...groupWaypoint,
      banks: selectedAttachedBankWaypoints,
      lifts: managedWaypoints,
      additionalLiftKeys: nextAdditionalLiftKeys,
    });

    setActiveGroupWaypoint(nextGroupWaypoint);
  };

  const applyGroupAdditionalLiftSelection = (groupWaypoint: GroupWaypoint, liftKeys: string[]) => {
    const center = resolveInsertionCenter(groupWaypoint);
    const selectedBankNames = resolveGroupSelectedBankNames(groupWaypoint, bankWaypoints);
    const selectedAttachedBankWaypoints = (groupWaypoint.banks || []).filter((bankWaypoint) =>
      selectedBankNames.includes(bankWaypoint.bankName ?? ""),
    );
    const selectedBanks = availableBanks.filter((bank) => selectedBankNames.includes(bank.name));
    const bankLiftKeys = new Set(
      selectedBanks.flatMap((bank) =>
        bank.lifts.map((liftName, liftIndex) => toLiftOptionKey(bank.name, liftName, liftIndex)),
      ),
    );
    const nextAdditionalLiftKeys = Array.from(
      new Set(
        liftKeys.map((liftKey) => String(liftKey)).filter((liftKey) => !bankLiftKeys.has(liftKey)),
      ),
    );
    const managedWaypoints = buildGroupManagedWaypoints(
      groupWaypoint,
      selectedBanks,
      availableBanks,
      nextAdditionalLiftKeys,
      [...waypoints, ...(groupWaypoint.lifts || [])],
      makeNewWaypoint,
    ).map((waypoint) => centerIfUnpinned(waypoint, center));

    const nextGroupWaypoint = calculateGroupBounds({
      ...groupWaypoint,
      banks: selectedAttachedBankWaypoints,
      lifts: managedWaypoints,
      additionalLiftKeys: nextAdditionalLiftKeys,
    });

    setActiveGroupWaypoint(nextGroupWaypoint);
  };

  const handleGroupManagedLiftChange = (waypointId: string, nextWaypoint: Waypoint) => {
    if (!activeGroupWaypoint) {
      return;
    }

    setActiveGroupManagedLifts((previous) =>
      previous.map((waypoint) =>
        waypoint.id === waypointId
          ? clampWaypointIntoGroupBounds(
              {
                ...nextWaypoint,
                liftGroup: activeGroupWaypoint.nickname,
              },
              activeGroupWaypoint,
            )
          : waypoint,
      ),
    );
  };

  const clampWaypointForGroup = (waypoint: Waypoint): Waypoint => {
    if (!waypoint.liftGroup) {
      return waypoint;
    }

    const activeGroupByName =
      activeGroupWaypoint && activeGroupWaypoint.nickname === waypoint.liftGroup
        ? activeGroupWaypoint
        : null;
    const persistedGroup = groupWaypoints.find(
      (groupWaypoint) => groupWaypoint.nickname === waypoint.liftGroup,
    );

    return clampWaypointIntoGroupBounds(waypoint, activeGroupByName || persistedGroup);
  };

  if (!/\/map-view\/editor\//.test(location.pathname)) {
    return null;
  }

  if (!validatePermission(Permission.ViewEdgeManagementMap, permissions)) {
    return <UnauthorizedPage />;
  }

  if (isLoadingGetMapView || isFetchingGetMapView) {
    return (
      <div className={`layout__contentPage__${theme}`}>
        <Card className="customTable" loading />
      </div>
    );
  }

  if (!selectedMap) {
    return (
      <div className={`layout__contentPage__${theme}`}>
        <Empty description={NO_MAP_FOUND_DESCRIPTION} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div className={`layout__contentPage__${theme}`}>
      <Card
        title="Map Preview"
        className="customTable"
        extra={
          <Space>
            <Button
              onClick={() => {
                setBankDrawerMode("create");
                setActiveBankWaypoint(makeNewBankWaypoint());
                setActiveBankLiftId(null);
                setOpenBankEditorDrawer(true);
                setOpenLiftEditorDrawer(false);
                setOpenGroupEditorDrawer(false);
                setIsPinMode(false);
                setPinTarget(null);
              }}
            >
              Add Bank
            </Button>
            <Button
              onClick={() => {
                setGroupDrawerMode("create");
                const newGroupWaypoint = makeNewGroupWaypoint();
                setActiveGroupWaypoint(newGroupWaypoint);
                setActiveGroupManagedLifts([]);
                setOpenGroupEditorDrawer(true);
                setOpenLiftEditorDrawer(false);
                setOpenBankEditorDrawer(false);
                setIsPinMode(false);
                setPinTarget(null);
              }}
            >
              Add Group
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setDrawerMode("create");
                setActiveWaypoint(makeNewWaypoint());
                setOpenLiftEditorDrawer(true);
                setOpenGroupEditorDrawer(false);
                setOpenBankEditorDrawer(false);
                setIsPinMode(false);
                setPinTarget(null);
              }}
            >
              Add Lift
            </Button>
            <Button type="default" loading={isLoadingPutMapView} onClick={saveMapChanges}>
              Save Map
            </Button>
          </Space>
        }
      >
        <SvgController
          svgSrc={resolvePublicAssetUrl(selectedMap.mapImgUrl)}
          alt={selectedMap.mapName}
          waypoints={displayedWaypoints}
          groupWaypoints={displayedGroupWaypoints}
          isLiftEditorOpen={openLiftEditorDrawer || openBankEditorDrawer}
          isGroupEditorOpen={openGroupEditorDrawer || openBankEditorDrawer}
          activeWaypointId={openBankEditorDrawer ? activeBankLiftId : (activeWaypoint?.id ?? null)}
          activeGroupWaypointId={
            openBankEditorDrawer
              ? (activeBankWaypoint?.id ?? null)
              : (activeGroupWaypoint?.id ?? null)
          }
          isPinMode={isPinMode}
          onWaypointResize={(waypointId, size) => {
            if (activeWaypoint && activeWaypoint.id === waypointId) {
              setActiveWaypoint(
                clampWaypointForGroup({
                  ...activeWaypoint,
                  x: size.x,
                  y: size.y,
                  backgroundWidth: size.width,
                  backgroundHeight: size.height,
                }),
              );
              return;
            }

            if (!activeBankWaypoint || !activeBankLiftId || activeBankLiftId !== waypointId) {
              return;
            }

            const targetLift = activeBankWaypoint.lifts.find((lift) => lift.id === waypointId);
            if (!targetLift) {
              return;
            }

            const resizedBank = updateBankLift(activeBankWaypoint, waypointId, (lift) => ({
              ...lift,
              x: size.x,
              y: size.y,
              backgroundWidth: size.width,
              backgroundHeight: size.height,
            }));

            if (
              !activeBankWaypoint.linkLifts ||
              activeBankWaypoint.x === null ||
              activeBankWaypoint.y === null
            ) {
              setActiveBankWaypoint(resizedBank);
              return;
            }

            const deltaX = (size.x ?? 0) - (targetLift.x ?? 0);
            const deltaY = (size.y ?? 0) - (targetLift.y ?? 0);
            const movedBank = applyBankDeltaMove(activeBankWaypoint, deltaX, deltaY);
            const oldLiftWidth = Math.max(targetLift.backgroundWidth, 1);
            const oldLiftHeight = Math.max(targetLift.backgroundHeight, 1);
            const scaleX = size.width / oldLiftWidth;
            const scaleY = size.height / oldLiftHeight;

            const scaledBank: BankWaypoint = {
              ...movedBank,
              backgroundWidth: Math.max(
                MIN_SHAPE_SIZE,
                Math.round(movedBank.backgroundWidth * Math.abs(scaleX)),
              ),
              backgroundHeight: Math.max(
                MIN_SHAPE_SIZE,
                Math.round(movedBank.backgroundHeight * Math.abs(scaleY)),
              ),
              lifts: movedBank.lifts.map((lift) => {
                if (
                  lift.x === null ||
                  lift.y === null ||
                  movedBank.x === null ||
                  movedBank.y === null
                ) {
                  return lift;
                }

                const relX = lift.x - movedBank.x;
                const relY = lift.y - movedBank.y;

                return {
                  ...lift,
                  x: Math.round(movedBank.x + relX * scaleX),
                  y: Math.round(movedBank.y + relY * scaleY),
                  backgroundWidth: Math.max(
                    MIN_SHAPE_SIZE,
                    Math.round(lift.backgroundWidth * Math.abs(scaleX)),
                  ),
                  backgroundHeight: Math.max(
                    MIN_SHAPE_SIZE,
                    Math.round(lift.backgroundHeight * Math.abs(scaleY)),
                  ),
                  iconWidth: Math.max(
                    MIN_ICON_SIZE,
                    Math.round((lift.iconWidth ?? DEFAULT_ICON_SIZE) * Math.abs(scaleX)),
                  ),
                  iconHeight: Math.max(
                    MIN_ICON_SIZE,
                    Math.round((lift.iconHeight ?? DEFAULT_ICON_SIZE) * Math.abs(scaleY)),
                  ),
                };
              }),
            };

            const finalizedBank = updateBankLift(scaledBank, waypointId, (lift) => ({
              ...lift,
              x: size.x,
              y: size.y,
              backgroundWidth: size.width,
              backgroundHeight: size.height,
            }));

            setActiveBankWaypoint(finalizedBank);
          }}
          onGroupWaypointResize={(groupWaypointId, size) => {
            if (activeGroupWaypoint && activeGroupWaypoint.id === groupWaypointId) {
              setActiveGroupWaypoint(
                calculateGroupBounds({
                  ...activeGroupWaypoint,
                  x: size.x,
                  y: size.y,
                  backgroundWidth: size.width,
                  backgroundHeight: size.height,
                }),
              );
              return;
            }

            if (!activeBankWaypoint || activeBankWaypoint.id !== groupWaypointId) {
              return;
            }

            const resized = withBankBounds({
              ...activeBankWaypoint,
              x: size.x,
              y: size.y,
              backgroundWidth: size.width,
              backgroundHeight: size.height,
            });

            setActiveBankWaypoint(applyBankResizeLinked(activeBankWaypoint, resized));
          }}
          onWaypointMove={(waypointId, coordinates) => {
            if (activeWaypoint && activeWaypoint.id === waypointId) {
              setActiveWaypoint(
                clampWaypointForGroup({
                  ...activeWaypoint,
                  x: coordinates.x,
                  y: coordinates.y,
                }),
              );
              return;
            }

            if (!activeBankWaypoint || !activeBankLiftId || activeBankLiftId !== waypointId) {
              return;
            }

            const targetLift = activeBankWaypoint.lifts.find((lift) => lift.id === waypointId);
            if (!targetLift) {
              return;
            }

            if (!activeBankWaypoint.linkLifts) {
              setActiveBankWaypoint(
                updateBankLift(activeBankWaypoint, waypointId, (lift) => ({
                  ...lift,
                  x: coordinates.x,
                  y: coordinates.y,
                })),
              );
              return;
            }

            const deltaX = coordinates.x - (targetLift.x ?? coordinates.x);
            const deltaY = coordinates.y - (targetLift.y ?? coordinates.y);
            setActiveBankWaypoint(applyBankDeltaMove(activeBankWaypoint, deltaX, deltaY));
          }}
          onGroupWaypointMove={(groupWaypointId, coordinates) => {
            if (activeGroupWaypoint && activeGroupWaypoint.id === groupWaypointId) {
              setActiveGroupWaypoint(
                calculateGroupBounds({
                  ...activeGroupWaypoint,
                  x: coordinates.x,
                  y: coordinates.y,
                }),
              );
              return;
            }

            if (!activeBankWaypoint || activeBankWaypoint.id !== groupWaypointId) {
              return;
            }

            const deltaX = coordinates.x - (activeBankWaypoint.x ?? coordinates.x);
            const deltaY = coordinates.y - (activeBankWaypoint.y ?? coordinates.y);

            setActiveBankWaypoint(applyBankDeltaMove(activeBankWaypoint, deltaX, deltaY));
          }}
          onWaypointRotate={(waypointId, rotations) => {
            if (activeWaypoint && activeWaypoint.id === waypointId) {
              setActiveWaypoint({
                ...activeWaypoint,
                backgroundRotationDeg: rotations.backgroundRotationDeg,
                iconRotationDeg: rotations.iconRotationDeg,
                nameRotationDeg: rotations.nameRotationDeg,
              });
              return;
            }

            if (!activeBankWaypoint || !activeBankLiftId || activeBankLiftId !== waypointId) {
              return;
            }

            const targetLift = activeBankWaypoint.lifts.find((lift) => lift.id === waypointId);
            if (!targetLift) {
              return;
            }

            if (
              !activeBankWaypoint.linkLifts ||
              activeBankWaypoint.x === null ||
              activeBankWaypoint.y === null
            ) {
              setActiveBankWaypoint(
                updateBankLift(activeBankWaypoint, waypointId, (lift) => ({
                  ...lift,
                  backgroundRotationDeg: rotations.backgroundRotationDeg,
                  iconRotationDeg: rotations.iconRotationDeg,
                  nameRotationDeg: rotations.nameRotationDeg,
                })),
              );
              return;
            }

            const delta = normalizeRotation(
              rotations.backgroundRotationDeg - (targetLift.backgroundRotationDeg ?? 0),
            );

            const rotatedBank: BankWaypoint = {
              ...activeBankWaypoint,
              backgroundRotationDeg: normalizeRotation(
                (activeBankWaypoint.backgroundRotationDeg ?? 0) + delta,
              ),
              nameRotationDeg: normalizeRotation((activeBankWaypoint.nameRotationDeg ?? 0) + delta),
              lifts: activeBankWaypoint.lifts.map((lift) => {
                if (
                  lift.x === null ||
                  lift.y === null ||
                  activeBankWaypoint.x === null ||
                  activeBankWaypoint.y === null
                ) {
                  return lift;
                }

                const nextPoint = rotatePoint(
                  lift.x,
                  lift.y,
                  activeBankWaypoint.x,
                  activeBankWaypoint.y,
                  delta,
                );

                return {
                  ...lift,
                  x: nextPoint.x,
                  y: nextPoint.y,
                  backgroundRotationDeg: normalizeRotation(
                    (lift.backgroundRotationDeg ?? 0) + delta,
                  ),
                  iconRotationDeg: normalizeRotation((lift.iconRotationDeg ?? 0) + delta),
                  nameRotationDeg: normalizeRotation((lift.nameRotationDeg ?? 0) + delta),
                };
              }),
            };

            setActiveBankWaypoint(
              updateBankLift(rotatedBank, waypointId, (lift) => ({
                ...lift,
                backgroundRotationDeg: rotations.backgroundRotationDeg,
                iconRotationDeg: rotations.iconRotationDeg,
                nameRotationDeg: rotations.nameRotationDeg,
              })),
            );
          }}
          onGroupWaypointRotate={(groupWaypointId, rotations) => {
            if (activeGroupWaypoint && activeGroupWaypoint.id === groupWaypointId) {
              setActiveGroupWaypoint(
                calculateGroupBounds({
                  ...activeGroupWaypoint,
                  backgroundRotationDeg: rotations.backgroundRotationDeg,
                  nameRotationDeg: rotations.nameRotationDeg,
                }),
              );
              return;
            }

            if (!activeBankWaypoint || activeBankWaypoint.id !== groupWaypointId) {
              return;
            }

            const rotated = withBankBounds({
              ...activeBankWaypoint,
              backgroundRotationDeg: rotations.backgroundRotationDeg,
              nameRotationDeg: rotations.nameRotationDeg,
            });

            setActiveBankWaypoint(applyBankRotateLinked(activeBankWaypoint, rotated));
          }}
          onPinWaypoint={(coordinates: { x: number; y: number }) => {
            if (pinTarget === "lift" && activeWaypoint) {
              setActiveWaypoint({
                ...activeWaypoint,
                x: coordinates.x,
                y: coordinates.y,
              });
            }

            if (pinTarget === "group" && activeGroupWaypoint) {
              setActiveGroupWaypoint(
                calculateGroupBounds({
                  ...activeGroupWaypoint,
                  x: coordinates.x,
                  y: coordinates.y,
                }),
              );
            }

            if (pinTarget === "bank" && activeBankWaypoint) {
              const pinnedBank = withBankBounds(
                placeBankLiftsAtPin(activeBankWaypoint, coordinates.x, coordinates.y),
              );
              setActiveBankWaypoint(pinnedBank);
              setActiveBankLiftId(pinnedBank.lifts[0]?.id ?? null);
            }

            setIsPinMode(false);
            setPinTarget(null);
          }}
          onWaypointSelect={(waypointId: string) => {
            if (openGroupEditorDrawer) {
              return;
            }

            if (openBankEditorDrawer) {
              if (activeBankWaypoint?.lifts.some((lift) => lift.id === waypointId)) {
                setActiveBankLiftId(waypointId);
              }
              return;
            }

            const selectedWaypoint = waypoints.find((item) => item.id === waypointId);
            if (selectedWaypoint) {
              setDrawerMode("edit");
              setActiveWaypoint(selectedWaypoint);
              setOpenLiftEditorDrawer(true);
              setOpenGroupEditorDrawer(false);
              setOpenBankEditorDrawer(false);
              setIsPinMode(false);
              setPinTarget(null);
              return;
            }

            const selectedBankByLift = findCommittedBankByLiftId(waypointId);
            if (!selectedBankByLift) {
              return;
            }

            setBankDrawerMode("edit");
            setActiveBankWaypoint(selectedBankByLift);
            setActiveBankLiftId(waypointId);
            setOpenBankEditorDrawer(true);
            setOpenLiftEditorDrawer(false);
            setOpenGroupEditorDrawer(false);
            setIsPinMode(false);
            setPinTarget(null);
          }}
          onGroupWaypointSelect={(groupWaypointId: string) => {
            if (openGroupEditorDrawer || openBankEditorDrawer) {
              return;
            }

            const selectedGroupWaypoint = groupWaypoints.find(
              (item) => item.id === groupWaypointId,
            );

            if (selectedGroupWaypoint) {
              const inferredSelections = inferGroupSelectionsFromManagedWaypoints(
                selectedGroupWaypoint,
                [...waypoints, ...(selectedGroupWaypoint.lifts || [])],
                bankWaypoints,
                availableBanks,
              );
              const hydratedGroupWaypoint: GroupWaypoint = {
                ...selectedGroupWaypoint,
                banks:
                  selectedGroupWaypoint.banks && selectedGroupWaypoint.banks.length > 0
                    ? selectedGroupWaypoint.banks
                    : inferredSelections.banks,
                additionalLiftKeys:
                  selectedGroupWaypoint.additionalLiftKeys &&
                  selectedGroupWaypoint.additionalLiftKeys.length > 0
                    ? selectedGroupWaypoint.additionalLiftKeys
                    : inferredSelections.additionalLiftKeys,
              };

              setGroupDrawerMode("edit");
              setActiveGroupWaypoint(hydratedGroupWaypoint);
              const selectedBankNames = resolveGroupSelectedBankNames(
                hydratedGroupWaypoint,
                bankWaypoints,
              );
              const selectedBanks = availableBanks.filter((bank) =>
                selectedBankNames.includes(bank.name),
              );
              setActiveGroupManagedLifts(
                clampWaypointsIntoGroupBounds(
                  buildGroupManagedWaypoints(
                    hydratedGroupWaypoint,
                    selectedBanks,
                    availableBanks,
                    hydratedGroupWaypoint.additionalLiftKeys || [],
                    [...waypoints, ...(hydratedGroupWaypoint.lifts || [])],
                    makeNewWaypoint,
                  ),
                  hydratedGroupWaypoint,
                ),
              );
              setOpenGroupEditorDrawer(true);
              setOpenLiftEditorDrawer(false);
              setOpenBankEditorDrawer(false);
              setIsPinMode(false);
              setPinTarget(null);
              return;
            }

            const selectedBank = bankWaypoints.find((item) => item.id === groupWaypointId);
            if (!selectedBank) {
              return;
            }

            setBankDrawerMode("edit");
            setActiveBankWaypoint(selectedBank);
            setActiveBankLiftId(selectedBank.lifts[0]?.id ?? null);
            setOpenBankEditorDrawer(true);
            setOpenLiftEditorDrawer(false);
            setOpenGroupEditorDrawer(false);
            setIsPinMode(false);
            setPinTarget(null);
          }}
          onViewportCenterChange={setEditorViewportCenter}
        />
      </Card>

      {activeWaypoint && (
        <LiftEditorDrawer
          open={openLiftEditorDrawer}
          loading={isLiftSaveFlowLoading}
          waypoint={activeWaypoint}
          mode={drawerMode}
          isPinMode={isPinMode && pinTarget === "lift"}
          lockAspectRatio={Boolean(
            activeWaypoint.linkedBank && linkedBankLockMap[activeWaypoint.linkedBank],
          )}
          availableLiftOptions={liftLinkOptions}
          onWaypointChange={(nextWaypoint) => {
            if (isLiftSaveFlowLoading) {
              return;
            }
            setActiveWaypoint(clampWaypointForGroup(nextWaypoint));
          }}
          onTogglePinMode={() => {
            if (isLiftSaveFlowLoading) {
              return;
            }

            if (isPinMode && pinTarget === "lift") {
              setIsPinMode(false);
              setPinTarget(null);
              return;
            }

            setPinTarget("lift");
            setIsPinMode(true);
          }}
          onSave={async () => {
            if (isLiftSaveFlowLoading) {
              return;
            }

            setIsLiftSaveFlowLoading(true);
            try {
              // Save sequence is strict: waypoint first, then map snapshot, then close drawer.
              const persistedWaypoint = await persistWaypoint(activeWaypoint, drawerMode);
              const nextWaypoints = upsertWaypointById(waypoints, persistedWaypoint);

              await persistMapSnapshot(nextWaypoints, groupWaypoints, bankWaypoints);

              setWaypoints(nextWaypoints);

              setOpenLiftEditorDrawer(false);
              setIsPinMode(false);
              setPinTarget(null);
            } catch {
              message.error("Unable to save waypoint.");
            } finally {
              setIsLiftSaveFlowLoading(false);
            }
          }}
          onDelete={
            drawerMode === "edit"
              ? async () => {
                  try {
                    await removeWaypoint(activeWaypoint.id);

                    setWaypoints((previous) =>
                      previous.filter((item) => item.id !== activeWaypoint.id),
                    );
                    if (activeWaypoint.liftGroup) {
                      setGroupWaypoints((previous) =>
                        previous.map((groupWaypoint) =>
                          groupWaypoint.nickname === activeWaypoint.liftGroup
                            ? {
                                ...groupWaypoint,
                                lifts: Array.isArray(groupWaypoint.lifts)
                                  ? groupWaypoint.lifts.filter(
                                      (lift) => lift.id !== activeWaypoint.id,
                                    )
                                  : groupWaypoint.lifts,
                              }
                            : groupWaypoint,
                        ),
                      );
                    }

                    setOpenLiftEditorDrawer(false);
                    setIsPinMode(false);
                    setPinTarget(null);
                  } catch {
                    message.error("Unable to delete waypoint.");
                  }
                }
              : undefined
          }
          onClose={() => {
            if (isLiftSaveFlowLoading) {
              return;
            }

            setOpenLiftEditorDrawer(false);
            setIsPinMode(false);
            setPinTarget(null);
          }}
        />
      )}

      {isAnySaveFlowLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(15, 23, 42, 0.22)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            pointerEvents: "all",
          }}
        >
          <div
            style={{
              minWidth: 240,
              padding: "18px 22px",
              borderRadius: 10,
              backgroundColor: "rgba(255,255,255,0.95)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            }}
          >
            <Spin size="large" tip={saveFlowLabel} />
          </div>
        </div>
      )}

      {activeGroupWaypoint && (
        <GroupEditorDrawer
          open={openGroupEditorDrawer}
          loading={isGroupSaveFlowLoading}
          groupWaypoint={activeGroupWaypoint}
          mode={groupDrawerMode}
          isPinMode={isPinMode && pinTarget === "group"}
          linkedBankLockMap={linkedBankLockMap}
          availableBankOptions={organizationBankOptions}
          availableAdditionalLiftOptions={additionalLiftOptions}
          managedLifts={activeGroupManagedLifts}
          onGroupWaypointChange={(nextGroupWaypoint) => {
            if (isGroupSaveFlowLoading) {
              return;
            }

            const boundedGroupWaypoint = calculateGroupBounds(nextGroupWaypoint);
            setActiveGroupWaypoint(boundedGroupWaypoint);
            setActiveGroupManagedLifts((previous) =>
              clampWaypointsIntoGroupBounds(
                previous.map((waypoint) => ({
                  ...waypoint,
                  liftGroup: boundedGroupWaypoint.nickname,
                })),
                boundedGroupWaypoint,
              ),
            );
          }}
          onGroupBankSelectionChange={(bankNames) => {
            if (isGroupSaveFlowLoading) {
              return;
            }
            applyGroupBankSelection(activeGroupWaypoint, bankNames);
          }}
          onAdditionalLiftSelectionChange={(liftKeys) => {
            if (isGroupSaveFlowLoading) {
              return;
            }
            applyGroupAdditionalLiftSelection(activeGroupWaypoint, liftKeys);
          }}
          onManagedLiftChange={handleGroupManagedLiftChange}
          onTogglePinMode={() => {
            if (isGroupSaveFlowLoading) {
              return;
            }

            if (isPinMode && pinTarget === "group") {
              setIsPinMode(false);
              setPinTarget(null);
              return;
            }

            setPinTarget("group");
            setIsPinMode(true);
          }}
          onSave={async () => {
            if (isGroupSaveFlowLoading) {
              return;
            }

            setIsGroupSaveFlowLoading(true);
            try {
              // Save sequence is strict: group-related entities, then map snapshot, then close drawer.
              const boundedGroupWaypoint = calculateGroupBounds(activeGroupWaypoint);
              const managedPrefix = `group-lift-${boundedGroupWaypoint.id}-`;
              const additionalPrefix = `group-extra-lift-${boundedGroupWaypoint.id}-`;
              const committedGroupWaypoints = clampWaypointsIntoGroupBounds(
                activeGroupManagedLifts.map((waypoint) => ({
                  ...waypoint,
                  liftGroup: boundedGroupWaypoint.nickname,
                })),
                boundedGroupWaypoint,
              );

              const previousManagedWaypoints = waypoints.filter(
                (waypoint) =>
                  waypoint.id.startsWith(managedPrefix) || waypoint.id.startsWith(additionalPrefix),
              );
              const nextManagedWaypoints = committedGroupWaypoints;
              const previousManagedWaypointsById = new Map(
                previousManagedWaypoints.map((waypoint) => [waypoint.id, waypoint]),
              );

              const persistedManagedWaypoints = await Promise.all(
                nextManagedWaypoints.map((waypoint) => {
                  const mode = previousManagedWaypointsById.has(waypoint.id) ? "edit" : "create";
                  return persistWaypoint(waypoint, mode);
                }),
              );
              const persistedManagedWaypointsById = new Map(
                persistedManagedWaypoints.map((waypoint) => [waypoint.id, waypoint]),
              );

              await Promise.all(
                previousManagedWaypoints
                  .filter((waypoint) => !persistedManagedWaypointsById.has(waypoint.id))
                  .map((waypoint) => removeWaypoint(waypoint.id)),
              );

              const attachedBankWaypoints = bankWaypoints
                .filter((bankWaypoint) =>
                  (boundedGroupWaypoint.banks || []).some((bank) => bank.id === bankWaypoint.id),
                )
                .map((bankWaypoint) => ({
                  ...withBankBounds(bankWaypoint),
                  lifts: bankWaypoint.lifts.map((lift, liftIndex) => {
                    const sourceBank = availableBanks.find(
                      (bank) => bank.name === bankWaypoint.bankName,
                    );
                    return {
                      ...lift,
                      linkedBank: bankWaypoint.bankName,
                      linkedLift: resolveLiftLinkId(sourceBank, lift.nickname || "", liftIndex),
                    };
                  }),
                }));

              const persistedAttachedBankWaypoints = await Promise.all(
                attachedBankWaypoints.map((bankWaypoint) => {
                  const mode: "create" | "edit" = persistedBankWaypointKeys.includes(
                    bankWaypoint.id,
                  )
                    ? "edit"
                    : "create";
                  return persistBankWaypoint(bankWaypoint, mode);
                }),
              );

              const persistedGroupWaypoint = await persistGroupWaypoint(
                {
                  ...boundedGroupWaypoint,
                  lifts: persistedManagedWaypoints,
                  banks: persistedAttachedBankWaypoints,
                },
                groupDrawerMode,
              );

              const nextGroupWaypoint: GroupWaypoint = {
                ...persistedGroupWaypoint,
                lifts: persistedManagedWaypoints,
                banks: persistedAttachedBankWaypoints,
                additionalLiftKeys: boundedGroupWaypoint.additionalLiftKeys || [],
              };
              const nextWaypoints = [
                ...waypoints.filter(
                  (waypoint) =>
                    !waypoint.id.startsWith(managedPrefix) &&
                    !waypoint.id.startsWith(additionalPrefix),
                ),
                ...persistedManagedWaypoints,
              ];
              const nextGroupWaypoints = upsertGroupWaypointById(groupWaypoints, nextGroupWaypoint);
              const nextBankWaypoints = persistedAttachedBankWaypoints.reduce(
                (items, bankWaypoint) => upsertBankWaypointById(items, bankWaypoint),
                bankWaypoints,
              );

              await persistMapSnapshot(nextWaypoints, nextGroupWaypoints, nextBankWaypoints);

              setWaypoints(nextWaypoints);
              setGroupWaypoints(nextGroupWaypoints);
              setBankWaypoints(nextBankWaypoints);
              setPersistedBankWaypointKeys((previous) => {
                const committedBankIds = persistedAttachedBankWaypoints.map(
                  (bankWaypoint) => bankWaypoint.id,
                );
                return Array.from(new Set([...previous, ...committedBankIds]));
              });
              setActiveGroupManagedLifts([]);

              setOpenGroupEditorDrawer(false);
              setIsPinMode(false);
              setPinTarget(null);
            } catch {
              message.error("Unable to save group waypoint.");
            } finally {
              setIsGroupSaveFlowLoading(false);
            }
          }}
          onDelete={
            groupDrawerMode === "edit"
              ? async () => {
                  try {
                    const managedPrefix = `group-lift-${activeGroupWaypoint.id}-`;
                    const additionalPrefix = `group-extra-lift-${activeGroupWaypoint.id}-`;
                    const managedWaypoints = waypoints.filter(
                      (waypoint) =>
                        waypoint.id.startsWith(managedPrefix) ||
                        waypoint.id.startsWith(additionalPrefix),
                    );

                    await Promise.all(
                      managedWaypoints.map((waypoint) => removeWaypoint(waypoint.id)),
                    );
                    await removeGroupWaypoint(activeGroupWaypoint.id);

                    setWaypoints((previous) =>
                      previous.filter(
                        (waypoint) =>
                          !waypoint.id.startsWith(managedPrefix) &&
                          !waypoint.id.startsWith(additionalPrefix),
                      ),
                    );
                    setGroupWaypoints((previous) =>
                      previous.filter((item) => item.id !== activeGroupWaypoint.id),
                    );
                    setActiveGroupManagedLifts([]);

                    setOpenGroupEditorDrawer(false);
                    setIsPinMode(false);
                    setPinTarget(null);
                  } catch {
                    message.error("Unable to delete group waypoint.");
                  }
                }
              : undefined
          }
          onClose={() => {
            if (isGroupSaveFlowLoading) {
              return;
            }

            setOpenGroupEditorDrawer(false);
            setActiveGroupManagedLifts([]);
            setIsPinMode(false);
            setPinTarget(null);
          }}
        />
      )}

      {activeBankWaypoint && (
        <BankEditorDrawer
          open={openBankEditorDrawer}
          loading={isBankSaveFlowLoading}
          bankWaypoint={activeBankWaypoint}
          banks={availableBanks}
          mode={bankDrawerMode}
          isPinMode={isPinMode && pinTarget === "bank"}
          onBankWaypointChange={(nextBankWaypoint) => {
            if (isBankSaveFlowLoading) {
              return;
            }

            setActiveBankWaypoint(nextBankWaypoint);
          }}
          onActiveLiftSelect={setActiveBankLiftId}
          onTogglePinMode={() => {
            if (isBankSaveFlowLoading) {
              return;
            }

            if (isPinMode && pinTarget === "bank") {
              setIsPinMode(false);
              setPinTarget(null);
              return;
            }

            setPinTarget("bank");
            setIsPinMode(true);
          }}
          onSave={async () => {
            if (isBankSaveFlowLoading) {
              return;
            }

            setIsBankSaveFlowLoading(true);
            try {
              // Save sequence is strict: bank/lifts first, then map snapshot, then close drawer.
              const boundedBankWaypoint = withBankBounds({
                ...activeBankWaypoint,
              });
              const sourceBank = availableBanks.find(
                (bank) => bank.name === boundedBankWaypoint.bankName,
              );
              const nextLifts = boundedBankWaypoint.lifts.map((lift, liftIndex) => ({
                ...lift,
                linkedBank: boundedBankWaypoint.bankName,
                linkedLift: resolveLiftLinkId(sourceBank, lift.nickname || "", liftIndex),
              }));

              const persistedBankWaypoint = await persistBankWaypoint(
                {
                  ...boundedBankWaypoint,
                  lifts: nextLifts,
                },
                bankDrawerMode,
              );

              const nextBankWaypoint: BankWaypoint = {
                ...persistedBankWaypoint,
                lifts: nextLifts,
              };
              const nextBankWaypoints = upsertBankWaypointById(bankWaypoints, nextBankWaypoint);

              await persistMapSnapshot(waypoints, groupWaypoints, nextBankWaypoints);

              setBankWaypoints(nextBankWaypoints);
              setPersistedBankWaypointKeys((previous) =>
                Array.from(new Set([...previous, nextBankWaypoint.id])),
              );

              setOpenBankEditorDrawer(false);
              setIsPinMode(false);
              setPinTarget(null);
            } catch {
              message.error("Unable to save bank waypoint.");
            } finally {
              setIsBankSaveFlowLoading(false);
            }
          }}
          onDelete={
            bankDrawerMode === "edit"
              ? async () => {
                  try {
                    const previousBankWaypoint = bankWaypoints.find(
                      (item) => item.id === activeBankWaypoint.id,
                    );
                    await Promise.all(
                      (previousBankWaypoint?.lifts ?? []).map((lift) => removeWaypoint(lift.id)),
                    );

                    await removeBankWaypoint(activeBankWaypoint.id);

                    setBankWaypoints((previous) =>
                      previous.filter((item) => item.id !== activeBankWaypoint.id),
                    );
                    setPersistedBankWaypointKeys((previous) =>
                      previous.filter((bankWaypointId) => bankWaypointId !== activeBankWaypoint.id),
                    );
                    setGroupWaypoints((previous) =>
                      previous.map((groupWaypoint) => ({
                        ...groupWaypoint,
                        banks: Array.isArray(groupWaypoint.banks)
                          ? groupWaypoint.banks.filter(
                              (bankWaypoint) => bankWaypoint.id !== activeBankWaypoint.id,
                            )
                          : groupWaypoint.banks,
                      })),
                    );

                    setOpenBankEditorDrawer(false);
                    setIsPinMode(false);
                    setPinTarget(null);
                  } catch {
                    message.error("Unable to delete bank waypoint.");
                  }
                }
              : undefined
          }
          onClose={() => {
            if (isBankSaveFlowLoading) {
              return;
            }

            setOpenBankEditorDrawer(false);
            setIsPinMode(false);
            setPinTarget(null);
          }}
        />
      )}
    </div>
  );
}

export default MapEditorPage;
